<template>
  <div class="debt-installment-bill-pagination">
    <b-row>
      <!-- Total Count Display -->
      <b-col
        lg="3"
        md="3"
        sm="12"
      >
        <p
          class="debt-installment-bill-pagination__total mt-3 text-dark"
          :aria-label="`Tổng số bản ghi: ${total}`"
        >
          Tổng số: {{ total }}
        </p>
      </b-col>

      <!-- Pagination Controls -->
      <b-col
        lg="9"
        md="9"
        sm="12"
      >
        <b-pagination-nav
          class="debt-installment-bill-pagination__nav custom-pagination"
          v-show="totalPages >= 2"
          :link-gen="linkGenerator"
          :number-of-pages="totalPages"
          use-router
          @change="onPageChange"
          align="right"
          first-class="page-item-first btn btn-icon btn-sm m-1"
          prev-class="page-item-prev btn btn-icon btn-sm m-1"
          next-class="page-item-next btn btn-icon btn-sm m-1"
          last-class="page-item-last btn btn-icon btn-sm m-1"
          page-class="btn btn-icon btn-sm border-0 m-1"
          :aria-label="paginationAriaLabel"
        >
          <!-- First Page Button -->
          <template v-slot:first-text>
            <span :aria-label="firstPageAriaLabel">
              <i class="ki ki-bold-double-arrow-back icon-xs"></i>
            </span>
          </template>

          <!-- Previous Page Button -->
          <template v-slot:prev-text>
            <span :aria-label="prevPageAriaLabel">
              <i class="ki ki-bold-arrow-back icon-xs"></i>
            </span>
          </template>

          <!-- Next Page Button -->
          <template v-slot:next-text>
            <span :aria-label="nextPageAriaLabel">
              <i class="ki ki-bold-arrow-next icon-xs"></i>
            </span>
          </template>

          <!-- Last Page Button -->
          <template v-slot:last-text>
            <span
              class="text-info"
              :aria-label="lastPageAriaLabel"
            >
              <i class="ki ki-bold-double-arrow-next icon-xs"></i>
            </span>
          </template>
        </b-pagination-nav>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'DebtInstallmentBillPagination',
  props: {
    /**
     * Total number of items
     */
    total: {
      type: Number,
      default: 0,
      validator: (value) => value >= 0,
    },
    /**
     * Total number of pages
     */
    totalPages: {
      type: Number,
      default: 1,
      validator: (value) => value >= 1,
    },
    /**
     * Current page number
     */
    currentPage: {
      type: Number,
      default: 1,
      validator: (value) => value >= 1,
    },
    /**
     * Number of items per page
     */
    pageSize: {
      type: Number,
      default: 10,
      validator: (value) => value > 0,
    },
    /**
     * Current filter parameters for URL generation
     */
    filterParams: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Route name for pagination links
     */
    routeName: {
      type: String,
      default: 'list-debt-installment-bill',
    },
  },
  computed: {
    // Accessibility labels
    paginationAriaLabel() {
      return `Điều hướng phân trang, trang hiện tại: ${this.currentPage} / ${this.totalPages}`;
    },
    firstPageAriaLabel() {
      return 'Đi đến trang đầu tiên';
    },
    prevPageAriaLabel() {
      return 'Đi đến trang trước';
    },
    nextPageAriaLabel() {
      return 'Đi đến trang tiếp theo';
    },
    lastPageAriaLabel() {
      return 'Đi đến trang cuối cùng';
    },
  },
  methods: {
    linkGenerator(pageNum) {
      let params = this.buildUrlParams();
      params.page = pageNum || 1;

      return {
        name: this.routeName,
        query: _.omitBy(params, _.isNil),
      };
    },
    /**
     * Handle page change event
     */
    onPageChange(page) {
      this.$emit('page-change', page);
    },

    /**
     * Build URL parameters for pagination links
     */
    buildUrlParams() {
      const params = {
        page: this.currentPage,
        pageSize: this.pageSize,
        ...this.filterParams,
      };

      // Remove page and pageSize from filter params to avoid duplication
      return _.omit(params, ['page', 'pageSize']);
    },

    /**
     * Get current page info for accessibility
     */
    getCurrentPageInfo() {
      const startItem = (this.currentPage - 1) * this.pageSize + 1;
      const endItem = Math.min(this.currentPage * this.pageSize, this.total);

      return {
        startItem,
        endItem,
        total: this.total,
        currentPage: this.currentPage,
        totalPages: this.totalPages,
      };
    },

    /**
     * Check if pagination should be visible
     */
    shouldShowPagination() {
      return this.totalPages >= 2;
    },

    /**
     * Get page range for display
     */
    getPageRange() {
      const range = [];
      const maxVisible = 5; // Maximum number of page buttons to show

      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      // Adjust start if we're near the end
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        range.push(i);
      }

      return range;
    },

    /**
     * Navigate to specific page
     */
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.onPageChange(page);
      }
    },

    /**
     * Navigate to first page
     */
    goToFirstPage() {
      this.goToPage(1);
    },

    /**
     * Navigate to last page
     */
    goToLastPage() {
      this.goToPage(this.totalPages);
    },

    /**
     * Navigate to previous page
     */
    goToPrevPage() {
      this.goToPage(this.currentPage - 1);
    },

    /**
     * Navigate to next page
     */
    goToNextPage() {
      this.goToPage(this.currentPage + 1);
    },

    /**
     * Check if current page is first page
     */
    isFirstPage() {
      return this.currentPage === 1;
    },

    /**
     * Check if current page is last page
     */
    isLastPage() {
      return this.currentPage === this.totalPages;
    },
  },
};
</script>

<style lang="scss">
.debt-installment-bill-pagination {
  &__total {
    font-weight: 500;
  }

  &__nav {
    // Custom pagination styling
    .page-item.disabled {
      cursor: not-allowed;

      &.bv-d-xs-down-none {
        .page-link {
          margin: 0.35rem 0.25rem;
          text-align: center;
        }
      }
    }
  }
}
/* 
// Global pagination styles (for backward compatibility)
.custom-pagination {
  .page-item.disabled {
    cursor: not-allowed;

    &.bv-d-xs-down-none {
      .page-link {
        margin: 0.35rem 0.25rem;
        text-align: center;
      }
    }
  }
} */
</style>
